/**
 * GoogleMapEmbed
 * Layer: presentation/components (employees module)
 * Change: manejo-de-coordenadas-y-mapas
 * Change: fix-googlemaps-modals
 * Change: cambio-auto-seleccion-perfil-de-marcaje-y-varios-mas (Slice D — offline fallback)
 *
 * Renders a Google Map using @react-google-maps/api.
 * LoadScript is mounted ONCE in main.tsx (root) — this component only uses
 * GoogleMap + Marker directly, same pattern as legacy admin app.
 *
 * Behavior (conditional ladder — precedence in this exact order):
 *   1. No real coordinates OR no API key → "Sin coordenadas registradas"
 *      placeholder. Wins over offline because, even when connectivity is
 *      back, we still have no map to show.
 *   2. Offline (`navigator.onLine === false`) → localized offline notice
 *      ("El mapa no se puede cargar sin conexión a Internet"). Mirrors the
 *      no-coords placeholder visual treatment (same wrapper, same icon
 *      family, same typography).
 *   3. Online + valid coords + key → GoogleMap + Marker with skeleton overlay
 *      that disappears on `onTilesLoaded` / `onIdle`.
 *
 * Offline → online recovery: a `key` prop tied to `isOnline` is applied to
 * the map wrapper so the iframe remounts cleanly and fetches fresh tiles
 * without a full page reload. If QA ever reports stale tiles, a `mapKey`
 * counter incremented on transition is the documented fallback (see
 * design.md Slice D).
 */
import { useState, type CSSProperties } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { useOnlineStatus } from '@/shared/sync/useOnlineStatus';

interface GoogleMapEmbedProps {
  lat: number;
  lng: number;
  apiKey: string | undefined;
}

const MAP_CONTAINER_STYLE = {
  width: '100%',
  height: '130px',
  borderRadius: '10px',
  display: 'block',
} as const;

const SKELETON_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: 130,
  borderRadius: 10,
  background: '#F8F8F8',
  border: '1px solid #EBE9F1',
  zIndex: 1,
} as const;

// Shared placeholder visual: used by BOTH the no-coords placeholder and the
// offline notice. Extracted so both notices stay pixel-identical and any
// future visual tweak applies to both at once.
const PLACEHOLDER_STYLE: CSSProperties = {
  width: '100%',
  height: '130px',
  borderRadius: '10px',
  background: '#F8F8F8',
  border: '1px solid #EBE9F1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
} as const;

const PLACEHOLDER_LABEL_STYLE: CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  color: '#B9B9C3',
};

export function GoogleMapEmbed({ lat, lng, apiKey }: GoogleMapEmbedProps) {
  const { t } = useTranslation();
  const { isOnline } = useOnlineStatus();

  // Coerce to number in case strings slip through from the API
  const numLat = Number(lat);
  const numLng = Number(lng);
  const hasRealCoords = Number.isFinite(numLat) && Number.isFinite(numLng) &&
    (numLat !== 0 || numLng !== 0);

  const [tilesLoaded, setTilesLoaded] = useState(false);
  const handleLoaded = () => setTilesLoaded(true);

  // 1. No coords or no key → no-coords placeholder (takes precedence over offline)
  if (!hasRealCoords || !apiKey) {
    return (
      <div style={PLACEHOLDER_STYLE}>
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#B9B9C3" strokeWidth="2" strokeLinecap="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        <span style={PLACEHOLDER_LABEL_STYLE}>
          Sin coordenadas registradas
        </span>
      </div>
    );
  }

  // 2. Offline → offline notice (same visual treatment as no-coords placeholder,
  //    only the icon family and label differ).
  if (!isOnline) {
    return (
      <div data-testid="map-offline-notice" style={PLACEHOLDER_STYLE}>
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#B9B9C3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 1l22 22" />
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
          <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
        <span style={PLACEHOLDER_LABEL_STYLE}>
          {t('map.offlineNotice')}
        </span>
      </div>
    );
  }

  const center = { lat: numLat, lng: numLng };

  // 3. Online + valid coords + key → render the map.
  //    The `key={isOnline ? 'online' : 'offline'}` forces a clean remount of
  //    the iframe when connectivity returns so tiles reload from scratch.
  return (
    <div key="online" style={{ position: 'relative', width: '100%', height: 130 }}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={center}
        zoom={15}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
        onTilesLoaded={handleLoaded}
        onIdle={handleLoaded}
      >
        <Marker position={center} />
      </GoogleMap>
      {!tilesLoaded && (
        <div data-testid="map-skeleton" style={SKELETON_STYLE} />
      )}
    </div>
  );
}
